<script setup>
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';

const { t } = useI18n();

// Lista de variantes do teste A/B. O formulário base é a variante "A"
// (controle); cada variante aqui é B, C, … e sobrescreve conteúdo.
const variants = defineModel({ type: Array, required: true });

const nextKey = () => String.fromCharCode(66 + variants.value.length); // B, C, D…

const addVariant = () => {
  variants.value = [
    ...variants.value,
    {
      key: nextKey(),
      name: '',
      welcome_title: '',
      success_title: '',
      primary_color: '',
    },
  ];
};

const removeVariant = index => {
  variants.value = variants.value.filter((_, i) => i !== index);
};

const updateVariant = (index, patch) => {
  variants.value = variants.value.map((variant, i) =>
    i === index ? { ...variant, ...patch } : variant
  );
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.ABTEST.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('FORMS_MGMT.EDITOR.ABTEST.DESCRIPTION') }}
        </p>
      </div>
      <Button
        icon="i-lucide-plus"
        faded
        slate
        :label="t('FORMS_MGMT.EDITOR.ABTEST.ADD')"
        @click="addVariant"
      />
    </div>

    <div
      v-if="!variants.length"
      class="text-sm text-n-slate-11 border border-dashed border-n-weak rounded-md p-6 text-center"
    >
      {{ t('FORMS_MGMT.EDITOR.ABTEST.EMPTY') }}
    </div>

    <div
      v-for="(variant, index) in variants"
      :key="index"
      class="flex flex-col gap-3 border border-n-weak rounded-md p-4 bg-n-alpha-1"
    >
      <div class="flex items-center justify-between">
        <span
          class="px-2 py-0.5 text-xs font-semibold rounded-full bg-n-brand/15 text-n-brand"
        >
          {{ t('FORMS_MGMT.EDITOR.ABTEST.VARIANT', { key: variant.key }) }}
        </span>
        <Button
          v-tooltip.top="t('FORMS_MGMT.EDITOR.ABTEST.REMOVE')"
          icon="i-lucide-trash-2"
          ruby
          xs
          faded
          @click="removeVariant(index)"
        />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          :model-value="variant.name"
          :label="t('FORMS_MGMT.EDITOR.ABTEST.NAME')"
          :placeholder="t('FORMS_MGMT.EDITOR.ABTEST.KEEP')"
          @update:model-value="value => updateVariant(index, { name: value })"
        />
        <Input
          :model-value="variant.welcome_title"
          :label="t('FORMS_MGMT.EDITOR.ABTEST.WELCOME_TITLE')"
          :placeholder="t('FORMS_MGMT.EDITOR.ABTEST.KEEP')"
          @update:model-value="
            value => updateVariant(index, { welcome_title: value })
          "
        />
        <Input
          :model-value="variant.success_title"
          :label="t('FORMS_MGMT.EDITOR.ABTEST.SUCCESS_TITLE')"
          :placeholder="t('FORMS_MGMT.EDITOR.ABTEST.KEEP')"
          @update:model-value="
            value => updateVariant(index, { success_title: value })
          "
        />
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('FORMS_MGMT.EDITOR.ABTEST.PRIMARY_COLOR') }}
          </span>
          <ColorPicker
            :model-value="variant.primary_color"
            @update:model-value="
              value => updateVariant(index, { primary_color: value })
            "
          />
        </label>
      </div>
    </div>
  </div>
</template>
